/* eslint-disable camelcase */
import { SORT_ORDER, TFilterOptions } from '@evinced-private/ui-common';

import { preprocessSearchValue } from 'src/apps/mobile-common/helpers/MobileSearchHelper';
import { buildSeverityFilterVariables } from 'src/apps/mobile-common/helpers/MobileSeverityFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionOperators,
	Msdk_Report_RunsSortFields,
	Msdk_Report_RunsSortOrder,
	Msdk_Report_RunsTextSearchFields
} from 'src/common/types/graphql.generated';
import { ITableQueryParams } from 'src/common/types/TableQueryTypes';

import { MobileSdkTestsQueryVariables } from '../MobileSdkTests.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';

const testsTextSearchFields: Msdk_Report_RunsTextSearchFields[] = [
	Msdk_Report_RunsTextSearchFields.TestName,
	Msdk_Report_RunsTextSearchFields.RunTime,
	Msdk_Report_RunsTextSearchFields.CriticalIssues,
	Msdk_Report_RunsTextSearchFields.TotalIssues,
	Msdk_Report_RunsTextSearchFields.App,
	Msdk_Report_RunsTextSearchFields.AppVersion,
	Msdk_Report_RunsTextSearchFields.Os,
	Msdk_Report_RunsTextSearchFields.ProductVersion,
	Msdk_Report_RunsTextSearchFields.SdkType,
	Msdk_Report_RunsTextSearchFields.CustomFields,
	Msdk_Report_RunsTextSearchFields.DeviceManufacturer,
	Msdk_Report_RunsTextSearchFields.DeviceModel,
	Msdk_Report_RunsTextSearchFields.DeviceType,
	Msdk_Report_RunsTextSearchFields.OsVersion,
	Msdk_Report_RunsTextSearchFields.AppBuildNumber
];

const testsSortFieldNamesMap: Record<string, Msdk_Report_RunsSortFields> = {
	testName: Msdk_Report_RunsSortFields.TestNameAny,
	testDate: Msdk_Report_RunsSortFields.RunTime,
	runTime: Msdk_Report_RunsSortFields.RunTime,
	lastModified: Msdk_Report_RunsSortFields.RunTime,
	criticalIssues: Msdk_Report_RunsSortFields.CriticalIssues,
	totalIssues: Msdk_Report_RunsSortFields.TotalIssues,
	totalScans: Msdk_Report_RunsSortFields.TotalScans,
	app: Msdk_Report_RunsSortFields.AppAny,
	appName: Msdk_Report_RunsSortFields.AppAny,
	sdkType: Msdk_Report_RunsSortFields.SdkType,
	os: Msdk_Report_RunsSortFields.OsAny,
	platform: Msdk_Report_RunsSortFields.OsAny
};

const testsSortOrderMap: Record<SORT_ORDER, Msdk_Report_RunsSortOrder> = {
	[SORT_ORDER.ASC]: Msdk_Report_RunsSortOrder.Ascending,
	[SORT_ORDER.DESC]: Msdk_Report_RunsSortOrder.Descending
};

export const buildMobileSdkTestsQueryVariables = ({
	pageSize,
	page,
	sort,
	filterOptions
}: ITableQueryParams): MobileSdkTestsQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MobileSdkTestsQueryVariables>({
		sortFieldsMap: testsSortFieldNamesMap,
		sortOrderMap: testsSortOrderMap,
		searchFields: testsTextSearchFields
	});

	// Build core query parameters
	const searchValues = preprocessSearchValue(filterOptions?.searchValue ?? '', true);

	variablesBuilder
		.addPaginationParams(page, pageSize)
		.addSortParam(sort)
		.addTextSearchParam(searchValues);

	// Add dedicated filter fields
	if (filterOptions?.platform?.length) {
		variablesBuilder.addFilterParam('osFilter', filterOptions.platform);
	}

	if (filterOptions?.appName?.length) {
		variablesBuilder.addFilterParam('appFilter', filterOptions.appName);
	}

	if (filterOptions?.appVersion?.length) {
		variablesBuilder.addFilterParam('appVersionFilter', filterOptions.appVersion);
	}

	if (filterOptions?.sdkType?.length) {
		variablesBuilder.addFilterParam('sdkTypeFilter', filterOptions.sdkType);
	}

	if (filterOptions?.sdkVersion?.length) {
		variablesBuilder.addFilterParam('sdkVersionFilter', filterOptions.sdkVersion);
	}

	const knownFields = new Set([
		'sessionsPeriod',
		'searchValue',
		'platform',
		'appName',
		'appVersion',
		'sdkType',
		'sdkVersion',
		'severity'
	]);
	const customFields: TFilterOptions = {};

	if (filterOptions) {
		for (const [key, value] of Object.entries(filterOptions)) {
			if (!knownFields.has(key) && value && Array.isArray(value)) {
				customFields[key] = value;
			}
		}
	}

	// Handle custom fields if any exist
	if (Object.keys(customFields).length > 0) {
		variablesBuilder.addTupleFilterParam(
			'customFieldsFilter',
			customFields,
			ConditionOperators.All
		);
	}

	// Handle period filter
	const selectedPeriod = filterOptions?.sessionsPeriod?.[0];
	if (selectedPeriod?.value !== 'all') {
		variablesBuilder.addConditionParam(
			DAYS_AMOUNT_FILTER_PARAM_KEY,
			filterOptions.sessionsPeriod?.map((p) => Number(p.value))
		);
	}

	const variables = variablesBuilder.getVariables();
	const severityFilterVariables = buildSeverityFilterVariables<MobileSdkTestsQueryVariables>(
		filterOptions?.severity
	);

	return { ...variables, ...severityFilterVariables };
};
